/**
 * TASK-301: Factures Récurrentes - Formulaire de création/édition
 * Architecture 3 couches: Component (UI) → Hook (Logic) → API Layer (HTTP)
 * V2 DESIGN SYSTEM - Sprint 3
 *
 * Refactored: useClients() hook, useMutation, shared formatCurrency, PaymentMandateSelector
 */

import {
  ArrowLeft,
  Building2,
  Calendar,
  Check,
  CreditCard,
  FileText,
  Plus,
  Repeat,
  Save,
  Shield,
  Trash2,
  User,
  Zap,
} from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui-v2';
import { useClients } from '@/features/clients/hooks/useClients';
import { useAuthStore } from '@/stores/authStore';
import { formatCurrency } from '@/utils/vatCalculations';
import { PaymentMandateSelector } from '@/features/payments/components/PaymentMandateSelector';
import { PaymentMandateSetup } from '@/features/payments/components/PaymentMandateSetup';
import {
  useActiveClientMandate,
  useClientMandates,
} from '@/features/payments/hooks/usePaymentMandates';
import {
  useCreateRecurringInvoice,
  useUpdateRecurringInvoice,
} from '../hooks/useRecurringInvoices';
import type {
  RecurringInvoice,
  RecurringInvoiceTemplateLine,
  CreateRecurringInvoiceDTO,
  UpdateRecurringInvoiceDTO,
  RecurrenceFrequency,
} from '../types/billing.types';

interface RecurringInvoiceFormProps {
  template?: RecurringInvoice | null;
  onSuccess?: () => void;
}

// Frequency keys for i18n
const FREQUENCY_VALUES: RecurrenceFrequency[] = ['WEEKLY', 'BIWEEKLY', 'MONTHLY', 'BIMONTHLY', 'QUARTERLY', 'SEMIANNUAL', 'ANNUAL'];

// Default template line
const createEmptyLine = (): RecurringInvoiceTemplateLine => ({
  description: '',
  quantity: 1,
  unitPrice: 0,
  taxRate: 20,
  unit: 'unité',
});

/**
 * Selected mandate recap card — shows provider icon, masked IBAN/card, status badge
 * Provider labels are i18n keys resolved in the component
 */
const PROVIDER_ICONS: Record<string, React.ReactNode> = {
  GOCARDLESS: <Building2 className="w-5 h-5" />,
  STRIPE: <CreditCard className="w-5 h-5" />,
  MANUAL: <FileText className="w-5 h-5" />,
};

const PROVIDER_COLORS: Record<string, string> = {
  GOCARDLESS: 'text-blue-600 bg-blue-50',
  STRIPE: 'text-purple-600 bg-purple-50',
  MANUAL: 'text-gray-600 bg-gray-50',
};

const SelectedMandateCard: React.FC<{
  mandateId: string;
  clientId: string;
  onChange: () => void;
}> = ({ mandateId, clientId, onChange }) => {
  const { t } = useTranslation();
  const { data: mandates = [] } = useClientMandates(clientId);
  const mandate = mandates.find((m: any) => m.id === mandateId);

  if (!mandate) {
    return (
      <div className="bg-white/40 rounded-2xl border border-white/50 p-4 text-sm text-gray-500">
        {t('billing.recurring.mandateNotFound')}{' '}
        <button type="button" onClick={onChange} className="text-teal-600 font-medium hover:underline">
          {t('billing.recurring.chooseAnother')}
        </button>
      </div>
    );
  }

  const providerIcon = PROVIDER_ICONS[mandate.provider] || PROVIDER_ICONS.MANUAL;
  const providerColor = PROVIDER_COLORS[mandate.provider] || PROVIDER_COLORS.MANUAL;
  const providerLabel = t(`billing.provider.${mandate.provider}`);
  const maskedId = mandate.ibanLast4
    ? `**** **** **** ${mandate.ibanLast4}`
    : mandate.cardLast4
    ? `**** ${mandate.cardLast4}`
    : mandate.accountHolderName || t('billing.recurring.manualPayment');

  return (
    <div className="bg-white/60 backdrop-blur-sm rounded-2xl border border-white/50 p-4 flex items-center gap-4">
      <div className={`p-3 rounded-xl ${providerColor}`}>
        {providerIcon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="font-medium text-gray-900 truncate">{maskedId}</p>
          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${providerColor}`}>
            {providerLabel}
          </span>
        </div>
        <div className="flex items-center gap-1.5 mt-1">
          <Check className="w-3.5 h-3.5 text-emerald-500" />
          <span className="text-xs text-emerald-600 font-medium">{t('billing.recurringStatus.ACTIVE')}</span>
        </div>
      </div>
      <button
        type="button"
        onClick={onChange}
        className="px-3 py-1.5 text-sm text-teal-600 font-medium hover:bg-teal-50 rounded-lg transition-colors"
      >
        {t('common.change')}
      </button>
    </div>
  );
};

export const RecurringInvoiceForm: React.FC<RecurringInvoiceFormProps> = ({
  template,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isEditing = !!template;

  // Auth: get companyId for useClients hook
  const companyId = useAuthStore((state) => state.user?.companyId) || '';

  // Server state: clients via TanStack Query (F2 fix)
  const { data: clients = [], isLoading: loadingClients } = useClients(companyId);

  // Mutations via useMutation (F7 fix)
  const createMutation = useCreateRecurringInvoice();
  const updateMutation = useUpdateRecurringInvoice();

  // Form state
  const [formData, setFormData] = useState<CreateRecurringInvoiceDTO>({
    clientId: '',
    name: '',
    description: '',
    frequency: 'MONTHLY',
    startDate: new Date().toISOString().split('T')[0],
    endDate: undefined,
    templateLines: [createEmptyLine()],
    paymentTermsDays: 30,
    notes: '',
    termsConditions: '',
    autoSend: false,
  });

  // Payment mandate state
  const [autoPaymentEnabled, setAutoPaymentEnabled] = useState(false);
  const [selectedMandateId, setSelectedMandateId] = useState<string | null>(null);
  const [showMandateSetup, setShowMandateSetup] = useState(false);

  // Auto-select active mandate for client
  const { data: activeMandate } = useActiveClientMandate(
    autoPaymentEnabled && formData.clientId ? formData.clientId : undefined
  );

  // UI state
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Load template data if editing
  useEffect(() => {
    if (template) {
      setFormData({
        clientId: template.clientId,
        name: template.name,
        description: template.description || '',
        frequency: template.frequency,
        startDate: template.startDate.split('T')[0],
        endDate: template.endDate ? template.endDate.split('T')[0] : undefined,
        templateLines: template.templateLines.length > 0
          ? template.templateLines
          : [createEmptyLine()],
        paymentTermsDays: template.paymentTermsDays,
        notes: template.notes || '',
        termsConditions: template.termsConditions || '',
        autoSend: template.autoSend,
      });
      // Restore payment mandate state from template
      if (template.autoPayment) {
        setAutoPaymentEnabled(true);
      }
      if (template.paymentMandateId) {
        setSelectedMandateId(template.paymentMandateId);
      }
    }
  }, [template]);

  // Auto-select when activeMandate loads and no mandate is selected yet
  useEffect(() => {
    if (activeMandate && !selectedMandateId && autoPaymentEnabled) {
      setSelectedMandateId(activeMandate.id);
    }
  }, [activeMandate, selectedMandateId, autoPaymentEnabled]);

  // Handlers
  const handleChange = (field: keyof CreateRecurringInvoiceDTO, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleLineChange = (index: number, field: keyof RecurringInvoiceTemplateLine, value: any) => {
    setFormData(prev => {
      const newLines = [...prev.templateLines];
      newLines[index] = { ...newLines[index], [field]: value };
      return { ...prev, templateLines: newLines };
    });
  };

  const addLine = () => {
    setFormData(prev => ({
      ...prev,
      templateLines: [...prev.templateLines, createEmptyLine()],
    }));
  };

  const removeLine = (index: number) => {
    if (formData.templateLines.length <= 1) return;
    setFormData(prev => ({
      ...prev,
      templateLines: prev.templateLines.filter((_, i) => i !== index),
    }));
  };

  // Calculate totals (F8: marked as estimation)
  const calculateTotals = useCallback(() => {
    let totalHT = 0;
    let totalTVA = 0;

    formData.templateLines.forEach(line => {
      const lineHT = line.quantity * line.unitPrice;
      const lineTVA = lineHT * (line.taxRate / 100);
      totalHT += lineHT;
      totalTVA += lineTVA;
    });

    return {
      totalHT,
      totalTVA,
      totalTTC: totalHT + totalTVA,
    };
  }, [formData.templateLines]);

  const totals = calculateTotals();

  // Validation
  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.clientId) {
      newErrors.clientId = t('billing.recurring.validation.clientRequired');
    }
    if (!formData.name.trim()) {
      newErrors.name = t('billing.recurring.validation.nameRequired');
    }
    if (!formData.startDate) {
      newErrors.startDate = t('billing.recurring.validation.startDateRequired');
    }
    if (formData.templateLines.length === 0) {
      newErrors.templateLines = t('billing.recurring.validation.lineRequired');
    }

    // Validate each line
    formData.templateLines.forEach((line, index) => {
      if (!line.description.trim()) {
        newErrors[`line_${index}_description`] = t('billing.recurring.validation.descriptionRequired');
      }
      if (line.quantity <= 0) {
        newErrors[`line_${index}_quantity`] = t('billing.recurring.validation.quantityInvalid');
      }
      if (line.unitPrice < 0) {
        newErrors[`line_${index}_unitPrice`] = t('billing.recurring.validation.priceInvalid');
      }
    });

    // Validate auto-payment requires a mandate
    if (autoPaymentEnabled && !selectedMandateId) {
      newErrors.autoPayment = t('billing.recurring.validation.mandateRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Submit via useMutation (F7 fix) — includes autoPayment + paymentMandateId
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) return;

    // Build payload with payment fields
    const payload = {
      ...formData,
      autoPayment: autoPaymentEnabled && !!selectedMandateId,
      paymentMandateId: autoPaymentEnabled && selectedMandateId ? selectedMandateId : undefined,
    };

    if (isEditing && template) {
      const updatePayload: UpdateRecurringInvoiceDTO = {
        ...payload,
        // If auto-payment was disabled, disconnect mandate
        paymentMandateId: autoPaymentEnabled && selectedMandateId
          ? selectedMandateId
          : (template.paymentMandateId ? null : undefined),
      };

      updateMutation.mutate(
        { id: template.id, data: updatePayload },
        {
          onSuccess: () => {
            if (onSuccess) {
              onSuccess();
            } else {
              navigate('/recurring-invoices');
            }
          },
          onError: (err) => {
            setErrors({
              submit: err instanceof Error ? err.message : t('common.errorOccurred'),
            });
          },
        }
      );
    } else {
      createMutation.mutate(payload, {
        onSuccess: () => {
          if (onSuccess) {
            onSuccess();
          } else {
            navigate('/recurring-invoices');
          }
        },
        onError: (err) => {
          setErrors({
            submit: err instanceof Error ? err.message : t('common.errorOccurred'),
          });
        },
      });
    }
  };

  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="px-8 pt-8 pb-12 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/recurring-invoices')}
          className="flex items-center gap-2 text-gray-500 hover:text-gray-700 mb-4 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          {t('common.backToList')}
        </button>
        <div className="flex items-center gap-3">
          <div className="p-3 bg-teal-100 rounded-2xl">
            <Repeat className="w-6 h-6 text-teal-600" />
          </div>
          <div>
            <h1 className="text-3xl font-light tracking-tight text-gray-900">
              {isEditing ? t('billing.recurring.editTemplate') : t('billing.recurring.newTemplateTitle')}
            </h1>
            <p className="text-gray-500 mt-1">
              {t('billing.recurring.formSubtitle')}
            </p>
          </div>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Error banner */}
        {errors.submit && (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-4 text-red-700">
            {errors.submit}
          </div>
        )}

        {/* Basic Info Card */}
        <div className="bg-white/20 backdrop-blur-xl rounded-3xl shadow-xl shadow-gray-300/10 border border-white/30 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <User className="w-5 h-5 text-teal-600" />
            {t('billing.recurring.generalInfo')}
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Client */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('common.client')} <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.clientId}
                onChange={(e) => {
                  handleChange('clientId', e.target.value);
                  // Reset mandate selection when client changes
                  setSelectedMandateId(null);
                  setShowMandateSetup(false);
                }}
                disabled={loadingClients}
                className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-colors bg-white ${
                  errors.clientId ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <option value="">
                  {loadingClients ? t('common.loading') : t('common.selectClient')}
                </option>
                {clients.map((client: any) => (
                  <option key={client.id} value={client.id}>
                    {client.companyName || client.fullName} {client.email ? `(${client.email})` : ''}
                  </option>
                ))}
              </select>
              {errors.clientId && (
                <p className="mt-1 text-sm text-red-600">{errors.clientId}</p>
              )}
            </div>

            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('billing.recurring.templateName')} <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder={t('billing.recurring.templateNamePlaceholder')}
                className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-colors ${
                  errors.name ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-600">{errors.name}</p>
              )}
            </div>

            {/* Frequency */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('billing.recurring.frequency')} <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.frequency}
                onChange={(e) => handleChange('frequency', e.target.value as RecurrenceFrequency)}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-colors bg-white"
              >
                {FREQUENCY_VALUES.map((freq) => (
                  <option key={freq} value={freq}>
                    {t(`billing.frequency.${freq}`)}
                  </option>
                ))}
              </select>
            </div>

            {/* Description */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('billing.recurring.descriptionLabel')}
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                rows={2}
                placeholder={t('billing.recurring.descriptionPlaceholder')}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-colors resize-none"
              />
            </div>
          </div>
        </div>

        {/* Dates Card */}
        <div className="bg-white/20 backdrop-blur-xl rounded-3xl shadow-xl shadow-gray-300/10 border border-white/30 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-teal-600" />
            {t('billing.recurring.scheduling')}
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Start Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('billing.recurring.startDateLabel')} <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={formData.startDate}
                onChange={(e) => handleChange('startDate', e.target.value)}
                className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-colors ${
                  errors.startDate ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.startDate && (
                <p className="mt-1 text-sm text-red-600">{errors.startDate}</p>
              )}
            </div>

            {/* End Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('billing.recurring.endDateOptional')}
              </label>
              <input
                type="date"
                value={formData.endDate || ''}
                onChange={(e) => handleChange('endDate', e.target.value || undefined)}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-colors"
              />
            </div>

            {/* Payment Terms */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('billing.recurring.paymentTermsDays')}
              </label>
              <input
                type="number"
                min="0"
                value={formData.paymentTermsDays}
                onChange={(e) => handleChange('paymentTermsDays', parseInt(e.target.value) || 30)}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-colors"
              />
            </div>
          </div>
        </div>

        {/* Auto Payment Card — Premium UX (visible right after scheduling) */}
        {formData.clientId && (
          <div className="bg-white/20 backdrop-blur-xl rounded-3xl shadow-xl shadow-gray-300/10 border border-white/30 p-6 animate-fade-in-up">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <div className="p-2 bg-gradient-to-br from-teal-50 to-teal-100 rounded-xl">
                  <Zap className="w-5 h-5 text-teal-600" />
                </div>
                {t('billing.recurring.autoPayment')}
              </h2>
              {/* Toggle */}
              <button
                type="button"
                onClick={() => {
                  const newState = !autoPaymentEnabled;
                  setAutoPaymentEnabled(newState);
                  if (!newState) {
                    setSelectedMandateId(null);
                    setShowMandateSetup(false);
                  }
                }}
                className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors duration-300 ${
                  autoPaymentEnabled ? 'bg-teal-600' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition-transform duration-300 ${
                    autoPaymentEnabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            <p className="text-sm text-gray-500 mb-4">
              {t('billing.recurring.autoPaymentDescription')}
            </p>

            {autoPaymentEnabled && (
              <div className="space-y-4 pt-4 border-t border-white/30 animate-fade-in-up">
                {/* Selected mandate recap */}
                {selectedMandateId && !showMandateSetup ? (
                  <SelectedMandateCard
                    mandateId={selectedMandateId}
                    clientId={formData.clientId}
                    onChange={() => {
                      setSelectedMandateId(null);
                    }}
                  />
                ) : showMandateSetup ? (
                  <PaymentMandateSetup
                    clientId={formData.clientId}
                    clientName={
                      clients.find((c: any) => c.id === formData.clientId)?.companyName ||
                      clients.find((c: any) => c.id === formData.clientId)?.fullName
                    }
                    onComplete={(mandate) => {
                      setSelectedMandateId(mandate.id);
                      setShowMandateSetup(false);
                    }}
                    onCancel={() => setShowMandateSetup(false)}
                  />
                ) : (
                  <PaymentMandateSelector
                    clientId={formData.clientId}
                    value={selectedMandateId}
                    onChange={setSelectedMandateId}
                    onCreateNew={() => setShowMandateSetup(true)}
                    showCreateButton
                  />
                )}

                {/* Validation error */}
                {errors.autoPayment && (
                  <p className="text-sm text-red-600">{errors.autoPayment}</p>
                )}

                {/* Security badge */}
                <div className="flex items-center gap-2 text-xs text-gray-400">
                  <Shield className="w-3.5 h-3.5" />
                  <span>{t('billing.recurring.psd2Compliant')}</span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Template Lines Card */}
        <div className="bg-white/20 backdrop-blur-xl rounded-3xl shadow-xl shadow-gray-300/10 border border-white/30 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">
              {t('billing.recurring.templateLines')}
            </h2>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addLine}
              icon={<Plus className="w-4 h-4" />}
            >
              {t('billing.recurring.addLine')}
            </Button>
          </div>

          {errors.templateLines && (
            <p className="mb-4 text-sm text-red-600">{errors.templateLines}</p>
          )}

          <div className="space-y-4">
            {formData.templateLines.map((line, index) => (
              <div
                key={index}
                className="bg-white/40 border border-white/50 rounded-2xl p-4"
              >
                <div className="grid grid-cols-12 gap-3">
                  {/* Description */}
                  <div className="col-span-12 md:col-span-4">
                    <label className="block text-xs font-medium text-gray-500 mb-1">
                      {t('billing.recurring.descriptionLabel')}
                    </label>
                    <input
                      type="text"
                      value={line.description}
                      onChange={(e) => handleLineChange(index, 'description', e.target.value)}
                      placeholder={t('billing.recurring.descriptionLinePlaceholder')}
                      className={`w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent ${
                        errors[`line_${index}_description`] ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                  </div>

                  {/* Quantity */}
                  <div className="col-span-4 md:col-span-2">
                    <label className="block text-xs font-medium text-gray-500 mb-1">
                      {t('billing.recurring.quantityLabel')}
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={line.quantity}
                      onChange={(e) => handleLineChange(index, 'quantity', parseFloat(e.target.value) || 0)}
                      className={`w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent ${
                        errors[`line_${index}_quantity`] ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                  </div>

                  {/* Unit Price */}
                  <div className="col-span-4 md:col-span-2">
                    <label className="block text-xs font-medium text-gray-500 mb-1">
                      {t('billing.recurring.unitPriceHT')}
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={line.unitPrice}
                      onChange={(e) => handleLineChange(index, 'unitPrice', parseFloat(e.target.value) || 0)}
                      className={`w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent ${
                        errors[`line_${index}_unitPrice`] ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                  </div>

                  {/* Tax Rate */}
                  <div className="col-span-4 md:col-span-2">
                    <label className="block text-xs font-medium text-gray-500 mb-1">
                      {t('billing.recurring.vatPercent')}
                    </label>
                    <select
                      value={line.taxRate}
                      onChange={(e) => handleLineChange(index, 'taxRate', parseFloat(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white"
                    >
                      <option value={0}>0%</option>
                      <option value={5.5}>5.5%</option>
                      <option value={10}>10%</option>
                      <option value={20}>20%</option>
                    </select>
                  </div>

                  {/* Line Total */}
                  <div className="col-span-8 md:col-span-1 flex items-end">
                    <div className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm font-medium text-right">
                      {formatCurrency(line.quantity * line.unitPrice * (1 + line.taxRate / 100))}
                    </div>
                  </div>

                  {/* Remove Button */}
                  <div className="col-span-4 md:col-span-1 flex items-end justify-end">
                    <button
                      type="button"
                      onClick={() => removeLine(index)}
                      disabled={formData.templateLines.length <= 1}
                      className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Totals (F8 fix: marked as estimation) */}
          <div className="mt-6 pt-4 border-t border-gray-200">
            <div className="flex flex-col items-end gap-2">
              <p className="text-xs text-gray-400 italic mb-1">{t('billing.recurring.estimationNote')}</p>
              <div className="flex justify-between w-48">
                <span className="text-gray-500">{t('billing.recurring.totalHT')}</span>
                <span className="font-medium">{formatCurrency(totals.totalHT)}</span>
              </div>
              <div className="flex justify-between w-48">
                <span className="text-gray-500">{t('billing.recurring.tva')}</span>
                <span className="font-medium">{formatCurrency(totals.totalTVA)}</span>
              </div>
              <div className="flex justify-between w-48 pt-2 border-t border-gray-200">
                <span className="font-semibold text-gray-900">{t('billing.recurring.totalTTC')}</span>
                <span className="font-bold text-xl text-teal-600">{formatCurrency(totals.totalTTC)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Options Card */}
        <div className="bg-white/20 backdrop-blur-xl rounded-3xl shadow-xl shadow-gray-300/10 border border-white/30 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            {t('billing.recurring.options')}
          </h2>

          <div className="space-y-4">
            {/* Auto Send */}
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.autoSend}
                onChange={(e) => handleChange('autoSend', e.target.checked)}
                className="w-5 h-5 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
              />
              <div>
                <span className="font-medium text-gray-900">{t('billing.recurring.autoSend')}</span>
                <p className="text-sm text-gray-500">
                  {t('billing.recurring.autoSendDescription')}
                </p>
              </div>
            </label>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('billing.recurring.internalNotes')}
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => handleChange('notes', e.target.value)}
                rows={2}
                placeholder={t('billing.recurring.notesPlaceholder')}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-colors resize-none"
              />
            </div>

            {/* Terms */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('billing.recurring.paymentTerms')}
              </label>
              <textarea
                value={formData.termsConditions}
                onChange={(e) => handleChange('termsConditions', e.target.value)}
                rows={2}
                placeholder={t('billing.recurring.termsPlaceholder')}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-colors resize-none"
              />
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-4 justify-end">
          <Button
            type="button"
            variant="ghost"
            onClick={() => navigate('/recurring-invoices')}
          >
            {t('common.actions.cancel')}
          </Button>
          <Button
            type="submit"
            variant="secondary"
            loading={isSubmitting}
            icon={<Save className="w-5 h-5" />}
          >
            {isEditing ? t('billing.recurring.updateTemplate') : t('billing.recurring.createTemplateBtn')}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default RecurringInvoiceForm;
